import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import PropTypes from 'prop-types';
import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
// @mui
import { alpha, Avatar, Box, Drawer, Stack, Tooltip, Typography } from '@mui/material';
// hooks
import useResponsive from '../../../hooks/useResponsive';
// config
import { NAV } from '../../../config-global';
// components
import Scrollbar from '../../../components/scrollbar';
// auth
import AILegacyLogo from '../../../assets/logo.png';
import { useAuthContext } from '../../../auth/useAuthContext';
import getNavConfig from './config-navigation';

// ── Sidebar colour palette ────────────────────────────────────────────────────
const SIDEBAR_BG = '#141929';
const SIDEBAR_HOVER = alpha('#ffffff', 0.07);
const ACTIVE_BG = '#7B1D2E';
const ACTIVE_ARROW = '#C0303F';
const TEXT_PRIMARY = '#FFFFFF';
const TEXT_SECONDARY = alpha('#FFFFFF', 0.55);
const DIVIDER_COLOR = alpha('#FFFFFF', 0.1);

// ----------------------------------------------------------------------

NavVertical.propTypes = {
  openNav: PropTypes.bool,
  onCloseNav: PropTypes.func,
};

// ── NavItem ───────────────────────────────────────────────────────────────────
function NavItem({ item, active }) {
  const navigate = useNavigate();
  return (
    <Box
      onClick={() => navigate(item.path)}
      sx={{
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        px: 2,
        py: 1.1,
        mx: 1.5,
        mb: 0.5,
        borderRadius: 1.5,
        cursor: 'pointer',
        bgcolor: active ? ACTIVE_BG : 'transparent',
        transition: 'background 0.2s',
        '&:hover': { bgcolor: active ? ACTIVE_BG : SIDEBAR_HOVER },
      }}
    >
      {/* Left icon */}
      <Box sx={{ color: active ? TEXT_PRIMARY : TEXT_SECONDARY, display: 'flex', mr: 1.5, flexShrink: 0 }}>
        {item.icon}
      </Box>

      {/* Label */}
      <Typography
        variant="body2"
        sx={{
          flex: 1,
          fontWeight: active ? 700 : 400,
          fontSize: '0.82rem',
          color: active ? TEXT_PRIMARY : TEXT_SECONDARY,
          lineHeight: 1.3,
        }}
      >
        {item.title}
      </Typography>

      {/* Right chevron indicator for active */}
      {active && (
        <Box
          sx={{
            width: 6,
            height: 6,
            borderTop: `2px solid ${TEXT_PRIMARY}`,
            borderRight: `2px solid ${TEXT_PRIMARY}`,
            transform: 'rotate(45deg)',
            flexShrink: 0,
            opacity: 0.9,
          }}
        />
      )}

      {/* Active left-border accent */}
      {active && (
        <Box
          sx={{
            position: 'absolute',
            left: 0,
            top: '20%',
            height: '60%',
            width: 3,
            borderRadius: 4,
            bgcolor: ACTIVE_ARROW,
          }}
        />
      )}
    </Box>
  );
}
NavItem.propTypes = { item: PropTypes.object.isRequired, active: PropTypes.bool };

// ── NavVertical ───────────────────────────────────────────────────────────────
export default function NavVertical({ openNav, onCloseNav }) {
  const { pathname } = useLocation();
  const { user, logout } = useAuthContext();
  const navigate = useNavigate();
  const isDesktop = useResponsive('up', 'lg');

  useEffect(() => {
    if (openNav) onCloseNav();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  // initials for avatar — from the CMS user name, falling back to legacy fields
  const initials = (() => {
    const source = user?.UserName || `${user?.userFirstName || ''} ${user?.userLastName || ''}`;
    const parts = source.trim().split(/\s+/).filter(Boolean);
    if (!parts.length) return 'ME';
    return ((parts[0][0] || 'M') + (parts[1]?.[0] || parts[0][1] || 'E')).toUpperCase();
  })();

  // Pull nav items from config-navigation based on the signed-in user's role
  const navGroups = getNavConfig(user?.roleId ?? user?.RoleID ?? 3);

  const renderContent = (
    <Scrollbar
      sx={{
        height: 1,
        '& .simplebar-content': { height: 1, display: 'flex', flexDirection: 'column' },
      }}
    >
      <Stack alignItems="center" sx={{ pt: 1, pb: 1, px: 1, borderBottom: `1px solid ${DIVIDER_COLOR}` }}>
        <Box
          component="img"
          src={AILegacyLogo}
          alt="MEITD"
          sx={{ width: 150, objectFit: 'contain' }}
        />
      </Stack>
      <Box sx={{ pt: 2, flex: 1 }}>
        {navGroups.map((group, gi) => (
          <Box key={group.subheader || gi} sx={{ mb: 1 }}>
            {!!group.subheader && (
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  px: 2.5,
                  pb: 0.5,
                  color: TEXT_SECONDARY,
                  fontSize: '0.6rem',
                  fontWeight: 700,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                }}
              >
                {group.subheader}
              </Typography>
            )}
            {group.items.map((item) => (
              <NavItem key={item.title} item={item} active={pathname.startsWith(item.path)} />
            ))}
          </Box>
        ))}
      </Box>

      <Box sx={{ flexGrow: 1 }} />

      {/* ── User account / bottom ────────────────────────────────── */}
      <Box sx={{ borderTop: `1px solid ${DIVIDER_COLOR}`, px: 2, py: 2 }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Avatar
            sx={{
              width: 36,
              height: 36,
              bgcolor: alpha(ACTIVE_ARROW, 0.8),
              fontSize: '0.75rem',
              fontWeight: 700,
              color: '#fff',
              cursor: 'pointer',
              flexShrink: 0,
            }}
          >
            {initials}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="caption" sx={{ fontWeight: 700, color: TEXT_PRIMARY, display: 'block', fontSize: '0.75rem' }} noWrap>
              {user?.UserName ||
                (user?.userFirstName
                  ? `${user.userFirstName} ${user.userLastName || ''}`.trim()
                  : 'MEITD User')}
            </Typography>
            <Typography variant="caption" sx={{ color: TEXT_SECONDARY, fontSize: '0.63rem', textTransform: 'capitalize' }} noWrap>
              {user?.role || user?.Role || 'user'}
            </Typography>
          </Box>
          <Tooltip title="Sign out">
            <LogoutRoundedIcon
              onClick={() => { logout(); navigate('/'); }}
              sx={{ fontSize: 16, color: TEXT_SECONDARY, cursor: 'pointer', flexShrink: 0, '&:hover': { color: TEXT_PRIMARY } }}
            />
          </Tooltip>
        </Stack>
      </Box>
    </Scrollbar>
  );

  const drawerPaper = {
    width: NAV.W_DASHBOARD,
    bgcolor: SIDEBAR_BG,
    boxShadow: '4px 0 24px rgba(0,0,0,0.5)',
    borderRight: 'none',
  };

  return (
    <Box component="nav" sx={{ flexShrink: { lg: 0 }, width: { lg: NAV.W_DASHBOARD }, height: 1 }}>
      {isDesktop ? (
        <Drawer open variant="permanent" PaperProps={{ sx: { ...drawerPaper, zIndex: 1 } }}>
          {renderContent}
        </Drawer>
      ) : (
        <Drawer open={openNav} onClose={onCloseNav} ModalProps={{ keepMounted: true }} PaperProps={{ sx: drawerPaper }}>
          {renderContent}
        </Drawer>
      )}
    </Box>
  );
}
